/**
 * 家族状态管理
 * 管理所有家族数据
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Family, FamilyMemberRecord, FamilyRole } from '../types/family'

// ============================================
// 状态定义
// ============================================

interface FamilyState {
  // 所有家族
  families: Map<string, Family>

  // 势力家族索引 (factionId -> familyIds[])
  factionFamilyIndex: Map<string, string[]>

  // 当前选中家族
  selectedFamilyId: string | null

  // ID计数器
  idCounter: number
}

interface FamilyActions {
  // 初始化
  initializeFamilies: (families: Family[]) => void
  resetFamilies: () => void

  // 家族操作
  addFamily: (family: Family) => void
  updateFamily: (familyId: string, updates: Partial<Family>) => void
  removeFamily: (familyId: string) => void

  // 批量操作
  addFamilies: (families: Family[]) => void
  removeFamiliesByFaction: (factionId: string) => void

  // 查询
  getFamily: (familyId: string) => Family | undefined
  getFamiliesByFaction: (factionId: string) => Family[]
  getRulingFamily: (factionId: string) => Family | undefined
  getFamilyByMember: (characterId: string) => Family | undefined

  // 成员操作
  addMemberToFamily: (familyId: string, characterId: string, name: string, relation: string) => void
  removeMemberFromFamily: (familyId: string, characterId: string) => void
  updateMemberRole: (familyId: string, characterId: string, role: FamilyRole) => void

  // 选择
  selectFamily: (familyId: string | null) => void

  // 统计
  getFactionFamilyCount: (factionId: string) => number
  getFamilyStats: (familyId: string) => {
    totalMembers: number
    aliveMembers: number
    deceasedMembers: number
    prestige: number
  }

  // 序列化/反序列化
  serialize: () => string
  deserialize: (data: string) => void
}

// ============================================
// Store 创建
// ============================================

export const useFamilyStore = create<FamilyState & FamilyActions>()(
  persist(
    (set, get) => ({
      // 初始状态
      families: new Map(),
      factionFamilyIndex: new Map(),
      selectedFamilyId: null,
      idCounter: 0,

      // ========================================
      // 初始化
      // ========================================

      initializeFamilies: (families: Family[]) => {
        const familyMap = new Map<string, Family>()
        const factionIndex = new Map<string, string[]>()

        families.forEach(family => {
          familyMap.set(family.id, family)

          // 建立势力索引
          if (!factionIndex.has(family.factionId)) {
            factionIndex.set(family.factionId, [])
          }
          factionIndex.get(family.factionId)!.push(family.id)
        })

        set({
          families: familyMap,
          factionFamilyIndex: factionIndex,
          idCounter: families.length,
        })
      },

      resetFamilies: () => {
        set({
          families: new Map(),
          factionFamilyIndex: new Map(),
          selectedFamilyId: null,
          idCounter: 0,
        })
      },

      // ========================================
      // 家族操作
      // ========================================

      addFamily: (family: Family) => {
        const { families, factionFamilyIndex } = get()

        // 添加家族
        const newFamilies = new Map(families)
        newFamilies.set(family.id, family)

        // 更新势力索引
        const newIndex = new Map(factionFamilyIndex)
        if (!newIndex.has(family.factionId)) {
          newIndex.set(family.factionId, [])
        }
        newIndex.get(family.factionId)!.push(family.id)

        set({
          families: newFamilies,
          factionFamilyIndex: newIndex,
          idCounter: get().idCounter + 1,
        })
      },

      updateFamily: (familyId: string, updates: Partial<Family>) => {
        const { families } = get()
        const family = families.get(familyId)
        if (!family) return

        const newFamilies = new Map(families)
        newFamilies.set(familyId, { ...family, ...updates })

        set({ families: newFamilies })
      },

      removeFamily: (familyId: string) => {
        const { families, factionFamilyIndex } = get()
        const family = families.get(familyId)
        if (!family) return

        // 移除家族
        const newFamilies = new Map(families)
        newFamilies.delete(familyId)

        // 更新势力索引
        const newIndex = new Map(factionFamilyIndex)
        const factionFamilies = newIndex.get(family.factionId)
        if (factionFamilies) {
          const updated = factionFamilies.filter(id => id !== familyId)
          if (updated.length === 0) {
            newIndex.delete(family.factionId)
          } else {
            newIndex.set(family.factionId, updated)
          }
        }

        set({
          families: newFamilies,
          factionFamilyIndex: newIndex,
        })
      },

      // ========================================
      // 批量操作
      // ========================================

      addFamilies: (families: Family[]) => {
        families.forEach(family => {
          get().addFamily(family)
        })
      },

      removeFamiliesByFaction: (factionId: string) => {
        const { families, factionFamilyIndex } = get()
        const familyIds = factionFamilyIndex.get(factionId) || []

        const newFamilies = new Map(families)
        familyIds.forEach(id => {
          newFamilies.delete(id)
        })

        const newIndex = new Map(factionFamilyIndex)
        newIndex.delete(factionId)

        set({
          families: newFamilies,
          factionFamilyIndex: newIndex,
        })
      },

      // ========================================
      // 查询
      // ========================================

      getFamily: (familyId: string) => {
        return get().families.get(familyId)
      },

      getFamiliesByFaction: (factionId: string) => {
        const { families, factionFamilyIndex } = get()
        const familyIds = factionFamilyIndex.get(factionId) || []
        return familyIds.map(id => families.get(id)).filter((f): f is Family => f !== undefined)
      },

      getRulingFamily: (factionId: string) => {
        const families = get().getFamiliesByFaction(factionId)
        return families.find(f => f.isRulingFamily)
      },

      getFamilyByMember: (characterId: string) => {
        const { families } = get()
        return Array.from(families.values()).find(f =>
          f.memberIds.includes(characterId)
        )
      },

      // ========================================
      // 成员操作
      // ========================================

      addMemberToFamily: (familyId: string, characterId: string, name: string, relation: string) => {
        const { families } = get()
        const family = families.get(familyId)
        if (!family) return

        const newFamilies = new Map(families)
        const updatedFamily = { ...family }

        // 添加成员ID
        updatedFamily.memberIds = [...updatedFamily.memberIds, characterId]

        // 添加成员记录
        const newMember: FamilyMemberRecord = {
          characterId,
          name,
          relation,
          role: 'member',
          joinDate: { year: 1, month: 1, day: 1 },
          isAlive: true,
        }
        updatedFamily.members = [...updatedFamily.members, newMember]

        newFamilies.set(familyId, updatedFamily)
        set({ families: newFamilies })
      },

      removeMemberFromFamily: (familyId: string, characterId: string) => {
        const { families } = get()
        const family = families.get(familyId)
        if (!family) return

        const newFamilies = new Map(families)
        const updatedFamily = { ...family }

        // 移除成员ID
        updatedFamily.memberIds = updatedFamily.memberIds.filter(id => id !== characterId)

        // 移除成员记录
        updatedFamily.members = updatedFamily.members.filter(m => m.characterId !== characterId)

        newFamilies.set(familyId, updatedFamily)
        set({ families: newFamilies })
      },

      updateMemberRole: (familyId: string, characterId: string, role: FamilyRole) => {
        const { families } = get()
        const family = families.get(familyId)
        if (!family) return

        const newFamilies = new Map(families)
        const updatedFamily = { ...family }

        // 更新成员角色
        updatedFamily.members = updatedFamily.members.map(m =>
          m.characterId === characterId ? { ...m, role } : m
        )

        newFamilies.set(familyId, updatedFamily)
        set({ families: newFamilies })
      },

      // ========================================
      // 选择
      // ========================================

      selectFamily: (familyId: string | null) => {
        set({ selectedFamilyId: familyId })
      },

      // ========================================
      // 统计
      // ========================================

      getFactionFamilyCount: (factionId: string) => {
        const { factionFamilyIndex } = get()
        return (factionFamilyIndex.get(factionId) || []).length
      },

      getFamilyStats: (familyId: string) => {
        const { families } = get()
        const family = families.get(familyId)
        if (!family) {
          return {
            totalMembers: 0,
            aliveMembers: 0,
            deceasedMembers: 0,
            prestige: 0,
          }
        }

        const aliveMembers = family.members.filter(m => m.isAlive).length

        return {
          totalMembers: family.members.length,
          aliveMembers,
          deceasedMembers: family.members.length - aliveMembers,
          prestige: family.prestige || 0,
        }
      },

      // ========================================
      // 序列化/反序列化
      // ========================================

      serialize: () => {
        const { families } = get()
        const data = {
          families: Array.from(families.entries()),
          idCounter: get().idCounter,
        }
        return JSON.stringify(data)
      },

      deserialize: (data: string) => {
        try {
          const parsed = JSON.parse(data)
          const familyMap = new Map<string, Family>(parsed.families as unknown as [string, Family][])

          // 重建势力索引
          const factionIndex = new Map<string, string[]>()
          familyMap.forEach((family, id) => {
            if (!factionIndex.has(family.factionId)) {
              factionIndex.set(family.factionId, [])
            }
            factionIndex.get(family.factionId)!.push(id)
          })

          set({
            families: familyMap,
            factionFamilyIndex: factionIndex,
            idCounter: parsed.idCounter || 0,
          })
        } catch (error) {
          console.error('Failed to deserialize family data:', error)
        }
      },
    }),
    {
      name: 'family-storage',
      partialize: (state) => ({
        families: Array.from(state.families.entries()),
        idCounter: state.idCounter,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // 重建 Map
          state.families = new Map(state.families as unknown as [string, Family][])
        }
      },
    }
  )
)
